import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from './firebase';

const MEMBERS_COLLECTION = 'jogador_turma';
const INVITES_COLLECTION = 'turma_convites';
const TURMAS_COLLECTION = 'turmas';
const TURMA_GAMES_COLLECTION = 'jogo_turma';

const buildMembershipPayload = ({ turmaId, userId, admin = false }) => ({
  id_turma: turmaId,
  id_usuario: userId,
  admin,
  status: 1,
  gols: 0,
  assistencias: 0,
  vitorias: 0,
  empates: 0,
  derrotas: 0,
  qt_partidas: 0,
  jogosSemTomarGol: 0,
  influenciaVitoria: 0,
  overall: 65,
  created_at: serverTimestamp(),
  updated_at: serverTimestamp()
});

export const isUserInTurma = async (turmaId, userId) => {
  const membershipQuery = query(
    collection(db, MEMBERS_COLLECTION),
    where('id_turma', '==', turmaId),
    where('id_usuario', '==', userId)
  );
  const snapshot = await getDocs(membershipQuery);
  return !snapshot.empty;
};

export const addUserToTurma = async ({ turmaId, userId, admin = false }) => {
  const alreadyMember = await isUserInTurma(turmaId, userId);
  if (alreadyMember) {
    return false;
  }
  await addDoc(
    collection(db, MEMBERS_COLLECTION),
    buildMembershipPayload({ turmaId, userId, admin })
  );
  return true;
};

export const getTurmaMembershipForUser = async ({ turmaId, userId }) => {
  if (!turmaId || !userId) return null;
  const membershipQuery = query(
    collection(db, MEMBERS_COLLECTION),
    where('id_turma', '==', turmaId),
    where('id_usuario', '==', userId)
  );
  const snapshot = await getDocs(membershipQuery);
  if (snapshot.empty) {
    return null;
  }
  const docSnapshot = snapshot.docs[0];
  return {
    id: docSnapshot.id,
    ...docSnapshot.data()
  };
};

const fetchTurma = async (turmaId) => {
  if (!turmaId) return null;
  const turmaRef = doc(db, TURMAS_COLLECTION, turmaId);
  const snapshot = await getDoc(turmaRef);
  if (!snapshot.exists()) {
    return null;
  }
  return { id: snapshot.id, ...snapshot.data() };
};

export const inviteUserToTurma = async ({ turmaId, turmaNome, invitedUser, inviter }) => {
  if (!turmaId || !invitedUser || !inviter) {
    throw new Error('Dados inválidos para convite.');
  }

  const turmaData = await fetchTurma(turmaId);
  if (!turmaData) {
    throw new Error('Turma não encontrada.');
  }
  if (turmaData.status !== 1) {
    throw new Error('A turma não está ativa para receber novos membros.');
  }

  const alreadyMember = await isUserInTurma(turmaId, invitedUser.id);
  if (alreadyMember) {
    throw new Error('Esse jogador já faz parte da turma.');
  }

  const existingInviteQuery = query(
    collection(db, INVITES_COLLECTION),
    where('id_turma', '==', turmaId),
    where('id_usuario_convidado', '==', invitedUser.id),
    where('status', '==', 'pendente')
  );
  const existingInviteSnapshot = await getDocs(existingInviteQuery);
  if (!existingInviteSnapshot.empty) {
    throw new Error('Esse jogador já possui um convite pendente.');
  }

  await addDoc(collection(db, INVITES_COLLECTION), {
    id_turma: turmaId,
    turma_nome: turmaNome || turmaData.nome,
    id_usuario_convidado: invitedUser.id,
    convidado_nome: invitedUser.nome,
    convidado_email: invitedUser.email,
    id_usuario_convite: inviter.uid,
    status: 'pendente',
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  });
};

export const fetchPendingTurmaInvites = async (userId) => {
  if (!userId) return [];
  const invitesSnapshot = await getDocs(
    query(
      collection(db, INVITES_COLLECTION),
      where('id_usuario_convidado', '==', userId),
      where('status', '==', 'pendente')
    )
  );

  return invitesSnapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data()
  }));
};

export const respondToTurmaInvite = async ({ inviteId, accept, userId }) => {
  const inviteRef = doc(db, INVITES_COLLECTION, inviteId);
  const inviteSnapshot = await getDoc(inviteRef);
  if (!inviteSnapshot.exists()) {
    throw new Error('Convite não encontrado.');
  }
  const inviteData = inviteSnapshot.data();
  if (accept) {
    const turmaData = await fetchTurma(inviteData.id_turma);
    if (!turmaData || turmaData.status !== 1) {
      throw new Error('Esta turma não está ativa para novos integrantes.');
    }
    await addUserToTurma({ turmaId: inviteData.id_turma, userId });
    await deleteDoc(inviteRef);
  } else {
    await updateDoc(inviteRef, { status: 'recusado', updated_at: serverTimestamp() });
  }
};

export const joinTurmaViaLink = async ({ turmaId, userId, senha }) => {
  if (!turmaId || !userId) {
    throw new Error('Dados inválidos para ingresso.');
  }

  const turmaData = await fetchTurma(turmaId);
  if (!turmaData) {
    throw new Error('Turma não encontrada.');
  }

  if (turmaData.status !== 1) {
    throw new Error('Esta turma não está ativa para novos membros.');
  }

  if (turmaData.protegida) {
    if (!senha) {
      throw new Error('Esta turma requer uma senha para entrar.');
    }
    if (turmaData.senha_grupo !== senha) {
      throw new Error('Senha da turma incorreta.');
    }
  }

  const added = await addUserToTurma({ turmaId, userId });
  if (!added) {
    throw new Error('Você já faz parte desta turma.');
  }
};

export const updateTurmaMemberStatus = async ({ membershipId, status }) => {
  if (!membershipId) {
    throw new Error('Integrante inválido.');
  }
  const memberRef = doc(db, MEMBERS_COLLECTION, membershipId);
  await updateDoc(memberRef, {
    status,
    updated_at: serverTimestamp()
  });
};

export const updateTurmaMemberAdmin = async ({ membershipId, admin }) => {
  if (!membershipId) {
    throw new Error('Integrante inválido.');
  }
  const memberRef = doc(db, MEMBERS_COLLECTION, membershipId);
  await updateDoc(memberRef, {
    admin: Boolean(admin),
    updated_at: serverTimestamp()
  });
};

export const removeTurmaMember = async (membershipId) => {
  if (!membershipId) {
    throw new Error('Integrante inválido.');
  }
  const memberRef = doc(db, MEMBERS_COLLECTION, membershipId);
  await deleteDoc(memberRef);
};

export const listTurmasForUser = async (userId) => {
  if (!userId) return [];
  const membershipsSnapshot = await getDocs(
    query(collection(db, MEMBERS_COLLECTION), where('id_usuario', '==', userId))
  );

  if (membershipsSnapshot.empty) {
    return [];
  }

  const turmas = await Promise.all(
    membershipsSnapshot.docs.map(async (docSnapshot) => {
      const membershipData = docSnapshot.data();
      try {
        const turmaRef = doc(db, 'turmas', membershipData.id_turma);
        const turmaDoc = await getDoc(turmaRef);
        if (!turmaDoc.exists()) {
          return null;
        }
        return { id: turmaDoc.id, ...turmaDoc.data() };
      } catch (error) {
        console.error('Erro ao carregar turma do usuário', error);
        return null;
      }
    })
  );

  return turmas.filter(Boolean);
};

export const updateTurmaSettings = async ({ turmaId, data }) => {
  if (!turmaId) {
    throw new Error('Turma inválida.');
  }
  if (!data) {
    throw new Error('Nenhuma alteração informada.');
  }
  const turmaRef = doc(db, TURMAS_COLLECTION, turmaId);
  const updates = {
    ...data,
    updated_at: serverTimestamp()
  };
  if (typeof updates.status !== 'undefined') {
    updates.status = Number(updates.status);
  }
  if (typeof updates.protegida !== 'undefined') {
    updates.protegida = Boolean(updates.protegida);
    if (updates.protegida) {
      if (!updates.senha_grupo) {
        throw new Error('Defina uma senha para turmas protegidas.');
      }
    } else {
      updates.senha_grupo = '';
    }
  }
  await updateDoc(turmaRef, updates);
};

const deleteRelatedDocs = async (collectionName, filters) => {
  const snapshot = await getDocs(query(collection(db, collectionName), ...filters));
  await Promise.all(snapshot.docs.map((docSnapshot) => deleteDoc(docSnapshot.ref)));
};

export const deleteTurmaAndData = async (turmaId) => {
  if (!turmaId) {
    throw new Error('Turma inválida.');
  }
  const turmaRef = doc(db, TURMAS_COLLECTION, turmaId);
  const turmaSnapshot = await getDoc(turmaRef);
  if (!turmaSnapshot.exists()) {
    throw new Error('Turma não encontrada.');
  }

  await Promise.all([
    deleteRelatedDocs(MEMBERS_COLLECTION, [where('id_turma', '==', turmaId)]),
    deleteRelatedDocs(INVITES_COLLECTION, [where('id_turma', '==', turmaId)]),
    deleteRelatedDocs(TURMA_GAMES_COLLECTION, [where('id_turma', '==', turmaId)])
  ]);

  await deleteDoc(turmaRef);
};
